import React, { useState } from 'react';
import {
  Users, Star, Clock, DollarSign, MessageSquare, Eye, Check, X,
  Filter, Search, Calendar, MapPin, Award, Leaf, Heart, Shield,
  TrendingUp, Download, Send, ChevronRight, AlertCircle,
  CheckCircle, FileText, Briefcase, Target, Zap
} from 'lucide-react';

const Applications = ({ company }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedApplications, setSelectedApplications] = useState([]);

  // Mock data pour les candidatures
  const applications = [
    {
      id: 1,
      freelancer: {
        name: "Camille Dupont",
        avatar: "CD",
        title: "Designer UX/UI Éco-responsable",
        rating: 4.9,
        reviewsCount: 127,
        location: "Paris, France",
        responseTime: "< 2h",
        completedProjects: 89,
        gradient: "from-blue-500 to-cyan-500"
      },
      project: {
        id: 1,
        title: "Refonte UX/UI App Mobile Éco-responsable",
        budget: 8500
      },
      application: {
        status: 'pending',
        appliedAt: '2024-01-15T10:30:00',
        proposedRate: 65,
        estimatedDuration: '6 semaines',
        coverLetter: "Passionnée par l'éco-conception, je serais ravie de contribuer à votre projet d'application mobile responsable. Mon expertise en design durable et accessibilité s'aligne parfaitement avec vos valeurs.",
        portfolio: [
          { title: "App GreenBank", type: "Mobile App" },
          { title: "Site EcoTech", type: "Website" }
        ],
        certifications: ["Label NR", "OPQUAST", "Carbon Literacy"],
        matchScore: 94,
        sustainabilityScore: 98
      }
    },
    {
      id: 2,
      freelancer: {
        name: "Thomas Bernard",
        avatar: "TB",
        title: "Développeur Full-Stack Engagé",
        rating: 4.8,
        reviewsCount: 203,
        location: "Lyon, France",
        responseTime: "< 1h",
        completedProjects: 156,
        gradient: "from-green-500 to-emerald-500"
      },
      project: {
        id: 2,
        title: "Développement Dashboard Analytics RSE",
        budget: 12000
      },
      application: {
        status: 'accepted',
        appliedAt: '2024-01-12T14:20:00',
        proposedRate: 75,
        estimatedDuration: '8 semaines',
        coverLetter: "Expert en développement web durable, je maîtrise les technologies nécessaires pour créer votre dashboard RSE performant et éco-conçu.",
        portfolio: [
          { title: "Dashboard RSE", type: "Web App" },
          { title: "API GreenData", type: "Backend" }
        ],
        certifications: ["B-Corp Dev", "Green IT", "AWS Certified"],
        matchScore: 96,
        sustainabilityScore: 95
      }
    },
    {
      id: 3,
      freelancer: {
        name: "Sophie Martin",
        avatar: "SM",
        title: "Data Scientist & Analyste Impact",
        rating: 4.9,
        reviewsCount: 94,
        location: "Remote",
        responseTime: "< 3h",
        completedProjects: 67,
        gradient: "from-purple-500 to-pink-500"
      },
      project: {
        id: 3,
        title: "Analyse Prédictive Impact Carbone",
        budget: 9500
      },
      application: {
        status: 'declined',
        appliedAt: '2024-01-10T09:15:00',
        proposedRate: 80,
        estimatedDuration: '5 semaines',
        coverLetter: "Spécialisée en analyse d'impact environnemental, je peux créer des modèles prédictifs précis pour optimiser votre empreinte carbone.",
        portfolio: [
          { title: "Modèle Prédictif Carbone", type: "ML Model" },
          { title: "Dashboard Impact", type: "Analytics" }
        ],
        certifications: ["Carbon Trust", "Data Science Ethics", "ISO 26000"],
        matchScore: 92,
        sustainabilityScore: 97
      }
    },
    {
      id: 4,
      freelancer: {
        name: "Marie Laurent",
        avatar: "ML",
        title: "Consultante Stratégie RSE",
        rating: 4.7,
        reviewsCount: 156,
        location: "Bordeaux, France",
        responseTime: "< 4h",
        completedProjects: 134,
        gradient: "from-emerald-500 to-teal-500"
      },
      project: {
        id: 1,
        title: "Refonte UX/UI App Mobile Éco-responsable",
        budget: 8500
      },
      application: {
        status: 'pending',
        appliedAt: '2024-01-14T16:45:00',
        proposedRate: 70,
        estimatedDuration: '7 semaines',
        coverLetter: "Mon approche stratégique de la RSE peut enrichir votre projet d'application mobile en intégrant des indicateurs d'impact dès la conception.",
        portfolio: [
          { title: "Stratégie RSE Tech", type: "Consulting" },
          { title: "Guide Green UX", type: "Framework" }
        ],
        certifications: ["ISO 26000", "B-Impact", "GRI Standards"],
        matchScore: 87,
        sustainabilityScore: 99
      }
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'text-orange-600 bg-orange-100';
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'declined': return 'text-red-600 bg-red-100';
      case 'interview': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Acceptée';
      case 'declined': return 'Refusée';
      case 'interview': return 'Entretien';
      default: return status;
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.freelancer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.project.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || app.application.status === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const handleApplicationAction = (applicationId, action) => {
    console.log(`${action} application ${applicationId}`);
    // Implement action logic here
  };

  const ApplicationCard = ({ application }) => (
    <div className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className={`w-14 h-14 bg-gradient-to-r ${application.freelancer.gradient} rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
              {application.freelancer.avatar}
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h3 className="font-bold text-gray-900 hover:text-green-600 transition-colors duration-200">
                  {application.freelancer.name}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(application.application.status)}`}>
                  {getStatusLabel(application.application.status)}
                </span>
              </div>
              <p className="text-gray-600 text-sm">{application.freelancer.title}</p>
              <div className="flex items-center mt-1 space-x-4">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-400 mr-1" />
                  <span className="text-sm font-medium text-gray-700">{application.freelancer.rating}</span>
                  <span className="text-xs text-gray-500 ml-1">({application.freelancer.reviewsCount})</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-xs text-gray-500">{application.freelancer.location}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-2">
              {new Date(application.application.appliedAt).toLocaleDateString('fr-FR')}
            </div>
            <div className="flex space-x-1">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Eye className="w-4 h-4 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Project Info */}
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900 text-sm">{application.project.title}</p>
              <p className="text-xs text-gray-500 mt-1">Budget: {application.project.budget.toLocaleString()}€</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-green-600">{application.application.proposedRate}€/h</p>
              <p className="text-xs text-gray-500">{application.application.estimatedDuration}</p>
            </div>
          </div>
        </div>

        {/* Match Scores */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <Target className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-gray-700">Match</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{application.application.matchScore}%</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <Leaf className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Impact</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{application.application.sustainabilityScore}%</div>
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Award className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">Certifications</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {application.application.certifications.map((cert) => (
              <span key={cert} className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg">
                {cert}
              </span>
            ))}
          </div>
        </div>

        {/* Cover Letter */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Message de motivation</h4>
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 line-clamp-3">
            {application.application.coverLetter}
          </p>
        </div>

        {/* Portfolio */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Portfolio pertinent</h4>
          <div className="grid grid-cols-2 gap-3">
            {application.application.portfolio.map((item, index) => (
              <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                <FileText className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {application.application.status === 'pending' && (
          <div className="flex space-x-3">
            <button 
              onClick={() => handleApplicationAction(application.id, 'accept')}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center justify-center"
            >
              <Check className="w-4 h-4 mr-2" />
              Accepter
            </button>
            <button className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 mr-2" />
              Entretien
            </button>
            <button 
              onClick={() => handleApplicationAction(application.id, 'decline')}
              className="px-4 py-3 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {application.application.status === 'accepted' && (
          <div className="flex space-x-3">
            <button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contacter
            </button>
            <button className="px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200">
              Voir le contrat
            </button>
          </div>
        )}

        {application.application.status === 'declined' && (
          <div className="text-center py-3">
            <p className="text-sm text-gray-500">Candidature refusée</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Users className="w-8 h-8 mr-3 text-green-500" />
            Candidatures
          </h1>
          <p className="text-gray-600">
            Gérez les candidatures de freelances engagés pour vos projets responsables.
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          <button className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
            <Download className="w-4 h-4 text-gray-600" />
            <span className="text-gray-700">Exporter</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">En attente</p>
              <p className="text-2xl font-bold text-orange-700">
                {applications.filter(app => app.application.status === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Acceptées</p>
              <p className="text-2xl font-bold text-green-700">
                {applications.filter(app => app.application.status === 'accepted').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Match moyen</p>
              <p className="text-2xl font-bold text-blue-700">
                {Math.round(applications.reduce((acc, app) => acc + app.application.matchScore, 0) / applications.length)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Score Impact</p>
              <p className="text-2xl font-bold text-purple-700">
                {Math.round(applications.reduce((acc, app) => acc + app.application.sustainabilityScore, 0) / applications.length)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Rechercher par freelance ou projet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <select 
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tous les projets</option>
              <option value="recent">Récentes</option>
              <option value="high-match">Match élevé</option>
              <option value="certified">Certifiés</option>
            </select>

            <button className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
              <Filter className="w-4 h-4 text-gray-600" />
              <span className="text-gray-700">Filtres</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mt-6 bg-gray-100 rounded-xl p-1">
          {[
            { id: 'all', label: 'Toutes', count: applications.length },
            { id: 'pending', label: 'En attente', count: applications.filter(app => app.application.status === 'pending').length },
            { id: 'accepted', label: 'Acceptées', count: applications.filter(app => app.application.status === 'accepted').length },
            { id: 'declined', label: 'Refusées', count: applications.filter(app => app.application.status === 'declined').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-green-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* IA Insights */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 mb-2">Insights IA</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Tendance positive</span>
                </div>
                <p className="text-xs text-gray-600">+23% de candidatures qualifiées ce mois</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Meilleur match</span>
                </div>
                <p className="text-xs text-gray-600">Camille Dupont - 94% de compatibilité</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-medium text-gray-700">Action recommandée</span>
                </div>
                <p className="text-xs text-gray-600">2 candidatures nécessitent une réponse</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="space-y-6">
        {filteredApplications.length > 0 ? (
          filteredApplications.map((application) => (
            <ApplicationCard key={application.id} application={application} />
          ))
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune candidature trouvée</h3>
            <p className="text-gray-600 mb-6">Publiez des projets attractifs pour recevoir des candidatures de freelances qualifiés.</p>
            <button className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200">
              <Briefcase className="w-5 h-5 mr-2 inline" />
              Publier un projet
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedApplications.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-center space-x-4">
          <span className="text-sm text-gray-600">{selectedApplications.length} sélectionnée(s)</span>
          <div className="flex space-x-2">
            <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200">
              Accepter
            </button>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200">
              Entretien
            </button>
            <button className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200">
              Refuser
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;